import React, { ReactNode, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, ButtonBase, Menu, MenuItem, Stack, Typography } from "@mui/material"
import type { PaperProps } from "@mui/material/Paper"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import type { TranslationKey } from "../../../modules/localization/translations"
import { TopBarNewButton } from "./TopBarNewButton"
import { TopBarNewButtonShortcuts, TopBarNewMenuShortcutConfig } from "./TopBarNewButtonShortcuts"

export type UserActionsMenuItemColor = "slate" | "teal" | "green" | "purple" | "amber" | "pink"

export type UserActionsMenuItemConfig = Readonly<{
    icon: IconDefinition
    color: UserActionsMenuItemColor
    title: ReactNode
    description?: ReactNode
    onClick: () => void
}>

export type TopBarNewMenuSection = Readonly<{
    id: string
    title: ReactNode
    items: ReadonlyArray<UserActionsMenuItemConfig>
    action?: Readonly<{
        icon: IconDefinition
        label: ReactNode
        onClick: () => void
    }>
}>

const useStyles = makeStyles()((theme) => ({
    menu: {
        maxWidth: "45rem",
        minWidth: "42rem",
        marginTop: ".5rem",
        borderRadius: ".5rem",
        boxShadow: "-1px 3px 10px 0 rgba(0, 0, 0, 0.14)",
        padding: "1.5rem 2rem",
        [theme.breakpoints.down("sm")]: {
            maxWidth: "calc(100vw - 2rem)",
            minWidth: 0,
            padding: "1rem",
        },
    },
    menuWithSections: {
        minWidth: "47.5rem",
        maxWidth: "48rem",
        padding: "1.75rem 1.75rem 1rem",
        [theme.breakpoints.down("md")]: {
            minWidth: 0,
            maxWidth: "calc(100vw - 2rem)",
        },
        [theme.breakpoints.down("sm")]: {
            padding: "1rem",
        },
    },
    menuListWithSections: {
        padding: 0,
    },
    menuItem: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: ".75rem",
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            whiteSpace: "normal",
        },
    },
    iconBox: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
    },
    iconSlate: {
        color: tailwindPalette.slate[900],
        background: tailwindPalette.slate[200],
    },
    iconTeal: {
        color: tailwindPalette.teal[900],
        background: tailwindPalette.teal[200],
    },
    iconGreen: {
        color: tailwindPalette.green[900],
        background: tailwindPalette.green[200],
    },
    iconPurple: {
        color: tailwindPalette.purple[900],
        background: tailwindPalette.purple[200],
    },
    iconAmber: {
        color: tailwindPalette.amber[900],
        background: tailwindPalette.amber[200],
    },
    iconPink: {
        color: tailwindPalette.pink[900],
        background: tailwindPalette.pink[200],
    },
    section: {
        padding: "0 .25rem",
        border: "1px solid transparent",
        borderRadius: ".75rem",
    },
    sectionOutlined: {
        paddingTop: "1rem",
        borderColor: tailwindPalette.slate[200],
    },
    sectionHeader: {
        padding: "0 .75rem",
    },
    sectionTitle: {
        // overline's default 2.66 is too tall for the mockup
        lineHeight: 1.5,
        fontWeight: theme.typography.fontWeightMedium,
        color: tailwindPalette.slate[600],
    },
    sectionAction: {
        color: tailwindPalette.slate[700],
        "&:hover .MuiTypography-root": {
            color: tailwindPalette.slate[900],
        },
    },
    sectionActionLabel: {
        fontWeight: theme.typography.fontWeightMedium,
        textDecoration: "underline",
        textUnderlineOffset: ".25rem",
    },
}))

type TopBarNewButtonMenuProps = Readonly<
    | {
          items: UserActionsMenuItemConfig[]
          shortcuts?: ReadonlyArray<TopBarNewMenuShortcutConfig>
      }
    | {
          sections: ReadonlyArray<TopBarNewMenuSection>
      }
>

export function TopBarNewButtonMenu(props: TopBarNewButtonMenuProps) {
    const { classes, cx } = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const open = Boolean(anchorEl)
    const hasSections = "sections" in props

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleAction = (onClick: () => void) => {
        handleMenuClose()
        onClick()
    }

    return (
        <>
            <TopBarNewButton onClick={handleMenuOpen} />

            <Menu
                slotProps={{
                    paper: {
                        className: cx(classes.menu, hasSections && classes.menuWithSections),
                        "data-tour": "merchant-product-tour-new-content",
                    } as PaperProps,
                }}
                MenuListProps={{ className: cx(hasSections && classes.menuListWithSections) }}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={open}
                onClose={handleMenuClose}
            >
                {"sections" in props ? (
                    <Stack gap={{ xs: ".75rem", sm: "1.25rem" }}>
                        {props.sections.map((section) => (
                            <Stack
                                key={section.id}
                                gap={section.action ? ".5rem" : 0}
                                className={cx(classes.section, section.action && classes.sectionOutlined)}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap=".5rem 1rem"
                                    className={classes.sectionHeader}
                                >
                                    <Typography variant="overline" className={classes.sectionTitle}>
                                        {section.title}
                                    </Typography>
                                    {section.action && (
                                        <ButtonBase
                                            className={classes.sectionAction}
                                            onClick={() => section.action && handleAction(section.action.onClick)}
                                        >
                                            <Stack direction="row" alignItems="center" gap=".5rem">
                                                <FontAwesomeIcon icon={section.action.icon} />
                                                <Typography variant="caption" className={classes.sectionActionLabel}>
                                                    {section.action.label}
                                                </Typography>
                                                <FontAwesomeIcon icon={faChevronRight} size="xs" />
                                            </Stack>
                                        </ButtonBase>
                                    )}
                                </Stack>
                                <TopBarNewMenuItemsGrid items={section.items} onItemClick={handleAction} />
                            </Stack>
                        ))}
                    </Stack>
                ) : (
                    <TopBarNewMenuItemsGrid items={props.items} onItemClick={handleAction}>
                        {props.shortcuts && props.shortcuts.length > 0 && (
                            <TopBarNewButtonShortcuts
                                shortcuts={props.shortcuts}
                                onShortcutClick={(shortcut) => handleAction(shortcut.onClick)}
                            />
                        )}
                    </TopBarNewMenuItemsGrid>
                )}
            </Menu>
        </>
    )
}

type TopBarNewMenuItemsGridProps = Readonly<{
    items: ReadonlyArray<UserActionsMenuItemConfig>
    onItemClick: (onClick: () => void) => void
    children?: ReactNode
}>

function TopBarNewMenuItemsGrid({ items, onItemClick, children }: TopBarNewMenuItemsGridProps) {
    const { classes, cx } = useStyles()

    const getIconColorClass = (color: UserActionsMenuItemColor) => {
        switch (color) {
            case "slate":
                return classes.iconSlate
            case "teal":
                return classes.iconTeal
            case "green":
                return classes.iconGreen
            case "purple":
                return classes.iconPurple
            case "amber":
                return classes.iconAmber
            case "pink":
                return classes.iconPink
            default:
                return classes.iconSlate
        }
    }

    return (
        <Box
            display="grid"
            gridTemplateColumns={{ xs: "minmax(0, 1fr)", sm: "1fr 1fr" }}
            gap={{ xs: ".5rem", sm: "1.5rem" }}
        >
            {items.map((item, index) => (
                <MenuItem className={classes.menuItem} onClick={() => onItemClick(item.onClick)} key={index}>
                    <Box className={cx(classes.iconBox, getIconColorClass(item.color))}>
                        <FontAwesomeIcon icon={item.icon} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1">{item.title}</Typography>
                        {item.description && (
                            <Typography variant="body2" color="textSecondary">
                                {item.description}
                            </Typography>
                        )}
                    </Box>
                </MenuItem>
            ))}

            {children}
        </Box>
    )
}
